// ajustes-1-3-0 (Bloque C3, #501, 25-sep-2026): BUSCAR POR NOMBRE O POR TEMA DENTRO DE UNA
// TARJETA PLEGABLE ABIERTA (la padre de las partes y la del grupo del profe).
//
// ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).
//
// La regla (#501): lo escrito filtra por CONTENIDO en el nombre mostrado O en el nombre de
// alguno de los temas, sin mayusculas ni acentos (la normalizacion de `buscarPorNombre.js`);
// vacio = todas. Una entrada que coincide SOLO por tema dice cuales temas coincidieron (la
// tarjeta pinta "Tema: X" bajo su nombre); si coincide por nombre, no se pinta el tema.
//
// LOS TEMAS NO VIAJAN AL CARGAR EL PANEL (sin brinco, regla 8 del encargo). Las cards del
// grupo ya los traen con el material (`temas: [{tema_id, nombre, count}]`) y filtran con
// eso. Las partes de la padre los piden UNA vez, la primera vez que el alumno escribe, con
// `useTemasParaBuscar`: `pedir(ids)` (en produccion `pedirTemasDeDocumentos`, que llama a
// GET /api/documents/temas?ids=...) y lo que llega se queda en memoria del componente
// mientras siga montado. Si la llamada falla (red, 5xx, 429), se deja de esperar, la
// busqueda sigue por nombre y se vuelve a pedir en la SIGUIENTE tecla (otro texto), nunca
// en un bucle sobre el mismo texto.
//
// remates-1-3-0c: (Q1) el tema que coincide se dice TAMBIEN cuando el nombre coincide; (Q2) un
// fallo se reintenta UNA vez a los 2 s y, si vuelve a fallar, la tarjeta lo dice bajo el cuadro
// (T1) en vez de callarlo; la siguiente tecla sigue reintentando.
//
// `pedirTemasDeDocumentos` importa `./api` de forma diferida, dentro de la funcion: esta lib
// no arrastra el cliente HTTP al cargarse (la leccion de `lib-nueva-que-importe-api`).
import { useEffect, useRef, useState } from 'react';
import { coincide } from './buscarPorNombre';

const SEPARADOR = ', ';

/** Ids unicos de una lista de entradas, en orden; sin vacios. */
export function idsPedibles(lista) {
  const out = [];
  (Array.isArray(lista) ? lista : []).forEach((x) => {
    const id = x && x.id;
    if (id && !out.includes(id)) out.push(id);
  });
  return out;
}

/**
 * Las que coinciden por nombre o por tema: `[{ item, temas }]`, con `temas` = los temas que
 * coincidieron (si ninguno, null). Copia, nunca la misma lista.
 *
 * remates-1-3-0c (Q1, W2): los temas coincidentes viajan SIEMPRE que haya alguno, aunque el
 * nombre tambien coincida. Decia: "SOLO cuando el nombre no coincide", y en "Grupo A" la card
 * cuyo nombre mostrado ES la lista de sus temas se quedaba sin su renglon "Tema: ...".
 */
export function filtrarPorNombreOTema(lista, escrito, nombreDe, temasDe) {
  const vacio = !escrito || !String(escrito).trim();
  return (Array.isArray(lista) ? lista : []).reduce((acc, item) => {
    if (vacio) { acc.push({ item, temas: null }); return acc; }
    const temas = temasDe ? temasDe(item) : null;
    const coincidentes = (Array.isArray(temas) ? temas : []).filter((t) => coincide(t, escrito));
    if (coincidentes.length) { acc.push({ item, temas: coincidentes }); return acc; }
    if (coincide(nombreDe(item), escrito)) acc.push({ item, temas: null });
    return acc;
  }, []);
}

/** Hasta dos temas separados por coma, y "..." si hay mas. */
export function resumenDeTemas(temas) {
  if (!Array.isArray(temas) || temas.length === 0) return '';
  const dos = temas.slice(0, 2).join(SEPARADOR);
  return temas.length > 2 ? `${dos}...` : dos;
}

/** GET /api/documents/temas?ids=a,b,c -> {id: [nombres]} (lib/api, diferido). */
export async function pedirTemasDeDocumentos(ids) {
  const { default: api } = await import('./api');
  const res = await api.get('/documents/temas', { params: { ids: ids.join(',') } });
  return res && res.data ? res.data : {};
}

// remates-1-3-0c (Q2 d): si la peticion falla (red, 5xx, 429), UN reintento a los 2 s; si
// vuelve a fallar, la tarjeta lo dice bajo el cuadro (T1) y la siguiente tecla reintenta.
export const REINTENTO_TEMAS_MS = 2000;

/**
 * Pide los temas UNA vez, la primera vez que hay algo escrito.
 * @param {string[]} ids        los ids de las entradas de la tarjeta
 * @param {string} escrito      lo escrito en el cuadro (vacio = no se pide nada)
 * @param {Function} pedir      (ids) => Promise<{id: [nombres]}>
 * @returns {{ temas: Map|null, cargando: boolean, fallo: boolean, temasDe: (item) => string[] }}
 *
 * remates-1-3-0c (Q2, W3.5). Lo que cambio, y por que:
 *   - `fallo`: tras el reintento, la peticion fallo; la tarjeta pinta T1 bajo el cuadro en vez de
 *     caer en silencio a buscar solo por nombre (el sintoma de Carlos: la ruta nunca contestaba y
 *     la tarjeta decia "Nada coincide.").
 *   - `vivo` se vuelve a encender al montar: `React.StrictMode` (src/index.js) monta, desmonta y
 *     vuelve a montar en desarrollo, y la limpieza lo dejaba apagado para siempre, asi que la
 *     respuesta se tiraba.
 *   - si cambian los ids (una parte termino de procesarse con la tarjeta abierta), lo que se
 *     tenia era de otras partes: se descarta y, con algo escrito, se vuelve a pedir; una
 *     respuesta que llega para la llave vieja se ignora.
 */
export function useTemasParaBuscar(ids, escrito, pedir) {
  const [temas, setTemas] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const enVuelo = useRef(false);
  const vivo = useRef(true);
  const reloj = useRef(null);
  // Cada llave pedida lleva su numero: una respuesta de un numero viejo no se aplica.
  const generacion = useRef(0);
  const llavePedida = useRef(null);
  // El texto con el que se intento la ultima vez: un fallo no se reintenta sobre el MISMO
  // texto (seria un bucle contra un 429); la siguiente tecla si.
  const intentado = useRef(null);
  const activo = !!(escrito && String(escrito).trim());
  const llave = idsPedibles(Array.isArray(ids) ? ids.map((id) => ({ id })) : []).join(',');

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
      if (reloj.current) { clearTimeout(reloj.current); reloj.current = null; }
    };
  }, []);

  useEffect(() => {
    if (llavePedida.current !== null && llavePedida.current !== llave) {
      // Otras partes: lo que habia (o lo que viene en camino) ya no sirve.
      generacion.current += 1;
      llavePedida.current = null;
      enVuelo.current = false;
      intentado.current = null;
      if (reloj.current) { clearTimeout(reloj.current); reloj.current = null; }
      setFallo(false);
      setCargando(false);
      if (temas !== null) { setTemas(null); return; }
    }
    if (!activo || temas !== null || enVuelo.current || !llave || typeof pedir !== 'function') return;
    if (intentado.current === escrito) return;
    intentado.current = escrito;
    enVuelo.current = true;
    llavePedida.current = llave;
    const mia = generacion.current;
    const vigente = () => vivo.current && generacion.current === mia;
    setCargando(true);
    setFallo(false);
    const unaVez = () => Promise.resolve().then(() => pedir(llave.split(',')));
    const esperar = () => new Promise((listo) => {
      reloj.current = setTimeout(() => { reloj.current = null; listo(); }, REINTENTO_TEMAS_MS);
    });
    unaVez()
      .catch(() => (vigente() ? esperar().then(unaVez) : Promise.reject(new Error('ya no vigente'))))
      .then((respuesta) => {
        if (!vigente()) return;
        const mapa = new Map();
        Object.entries(respuesta || {}).forEach(([id, nombres]) => {
          mapa.set(id, Array.isArray(nombres) ? nombres.filter((n) => typeof n === 'string' && n) : []);
        });
        setTemas(mapa);
      })
      .catch(() => { if (vigente()) setFallo(true); })
      .then(() => {
        if (generacion.current !== mia) return;
        enVuelo.current = false;
        if (vivo.current) setCargando(false);
      });
  }, [activo, escrito, llave, temas, pedir]);

  const temasDe = (item) => (temas && item && temas.get(item.id)) || [];
  return { temas, cargando, fallo, temasDe };
}
